import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { firstValueFrom } from 'rxjs';

export interface ExternalOverview {
  totalTvl: number;
  tvlChange24h: number;
  totalVolume24h: number;
  volumeChange24h: number;
  totalFees24h: number;
  feesChange24h: number;
  avgApr: number;
  aprChange24h: number;
  totalPools: number;
}

export interface ExternalToken {
  address: string;
  name: string;
  symbol: string;
  logoUrl: string;
  priceUsd: number;
  priceChange1h: number;
  priceChange1d: number;
  volume24h: number;
  fdv: number;
  chartData: any[];
}

export interface ExternalPool {
  address: string;
  token0: { symbol: string; address: string };
  token1: { symbol: string; address: string };
  feeTier: number;
  protocol: string;
  tvlUsd: number;
  volume24h: number;
  volume7d: number;
  apr: number;
  volTvlRatio: number;
}

export interface ExternalTransaction {
  time: string;
  type: string;
  usdValue: number;
  token0Amt: number;
  token1Amt: number;
  wallet: string;
  hash: string;
  protocol: string;
}

export interface PaginatedResponse<T> {
  status: boolean;
  data: T[];
  total: number;
  page: number;
  limit: number;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExternalLiquidityService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/external`;

  async getOverview(chainIds?: string): Promise<ExternalOverview> {
    const url = chainIds ? `${this.baseUrl}/overview?chainIds=${chainIds}` : `${this.baseUrl}/overview`;
    const res = await firstValueFrom(this.http.get<{ status: boolean, data: ExternalOverview }>(url));
    return res.data;
  }

  async getTokens(page = 1, limit = 10, search = '', chainIds?: string, sortBy?: string, sortDirection?: string): Promise<PaginatedResponse<ExternalToken>> {
    let url = `${this.baseUrl}/tokens?page=${page}&limit=${limit}`;
    if (search) url += `&search=${search}`;
    if (chainIds) url += `&chainIds=${chainIds}`;
    if (sortBy) url += `&sortBy=${sortBy}`;
    if (sortDirection) url += `&sortDirection=${sortDirection}`;
    
    return await firstValueFrom(this.http.get<PaginatedResponse<ExternalToken>>(url));
  }

  async getPools(page = 1, limit = 10, search = '', chainIds?: string, sortBy?: string, sortDirection?: string, feeTier?: number): Promise<PaginatedResponse<ExternalPool>> {
    let url = `${this.baseUrl}/pools?page=${page}&limit=${limit}`;
    if (search) url += `&search=${search}`;
    if (chainIds) url += `&chainIds=${chainIds}`;
    if (sortBy) url += `&sortBy=${sortBy}`;
    if (sortDirection) url += `&sortDirection=${sortDirection}`;
    if (feeTier !== undefined) url += `&feeTier=${feeTier}`;
    
    return await firstValueFrom(this.http.get<PaginatedResponse<ExternalPool>>(url));
  }

  async getTransactions(page = 1, limit = 10, chainIds?: string): Promise<PaginatedResponse<ExternalTransaction>> {
    let url = `${this.baseUrl}/transactions?page=${page}&limit=${limit}`;
    if (chainIds) url += `&chainIds=${chainIds}`;
    
    return await firstValueFrom(this.http.get<PaginatedResponse<ExternalTransaction>>(url));
  }
}
